// Scheduling verifies a technician stop (2026-09-05): a WORK trip to an address we do not have on file, or a PERSONAL
// trip to a customer site. Attach it to a ticket (as the job itself or a parts pickup, install or service), mark it
// personal, or dismiss. Opened from the Alerts card (alert kind trip_verify → url /directions?verify=<trip id>).
import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  MenuItem, Select, TextField, Typography,
} from '@mui/material';
import api from '../services/api';

type Props = { tripId: number | null; onClose: (changed: boolean) => void };

export default function TripVerifyDialog({ tripId, onClose }: Props) {
  const [trip, setTrip] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ticket, setTicket] = useState<any>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!tripId) return;
    setTrip(null); setErr(null); setTicket(null); setTicketType(''); setNote('');
    api.get(`/directions/trips/${tripId}`).then((r: any) => { setTrip(r.data.trip); setOptions(r.data.trip?.match?.tickets || []); })
      .catch((e: any) => setErr(e?.response?.data?.detail || 'Could not load the trip'));
  }, [tripId]);

  useEffect(() => {
    if (!q || q.length < 2) return;
    const h = setTimeout(() => {
      api.get('/tickets', { params: { search: q, limit: 10 } }).then((r: any) => {
        const list = r.data?.tickets || r.data?.items || r.data || [];
        setOptions([...(trip?.match?.tickets || []), ...list.filter((t: any) => !(trip?.match?.tickets || []).some((m: any) => m.id === t.id))]);
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(h);
  }, [q, trip]);

  const act = async (action: string) => {
    if (!tripId) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/directions/trips/${tripId}/verify`, { action, ticket_id: ticket?.id || null, ticket_type: ticketType || null, note: note || null });
      onClose(true);
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const m = trip?.match;
  return (
    <Dialog open={!!tripId} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Verify technician stop</DialogTitle>
      <DialogContent>
        {!trip && !err && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
        {err && <Alert severity="error" sx={{ mb: 2 }}>{String(err)}</Alert>}
        {trip && (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}><b>{trip.tech}</b> drove to <b>{trip.dest_address || trip.dest_label}</b></Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Claimed <b>{String(trip.classification).toUpperCase()}</b>{trip.ticket_number ? ` on ticket ${trip.ticket_number}` : ' with no ticket'} · started {trip.started_at ? new Date(trip.started_at).toLocaleString() : '—'}
              {trip.travel_miles ? ` · ${Number(trip.travel_miles).toFixed(1)} mi` : ''}
            </Typography>
            <Box sx={{ mb: 2 }}>
              {m?.customer ? (
                <Chip color="success" size="small" label={`Customer site: ${m.customer.company || m.customer.name} (${Math.round(m.distance_m)} m)`} />
              ) : (
                <Chip color="warning" size="small" label="Address is not a customer site we have on file" />
              )}
            </Box>
            <Autocomplete
              options={options}
              getOptionLabel={(o: any) => `${o.ticket_number} — ${o.title || ''}${o.mine ? ' (their ticket)' : ''}`}
              value={ticket}
              onChange={(_, v) => setTicket(v)}
              onInputChange={(_, v) => setQ(v)}
              renderInput={(p) => <TextField {...p} label="Ticket this stop belongs to" placeholder="Type a ticket number or title" />}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Set the ticket's type (optional)</InputLabel>
              <Select value={ticketType} label="Set the ticket's type (optional)" onChange={(e) => setTicketType(String(e.target.value))}>
                <MenuItem value="">Leave as is</MenuItem>
                <MenuItem value="INSTALLATION">Install</MenuItem>
                <MenuItem value="SERVICE">Service</MenuItem>
                <MenuItem value="REPAIR">Repair</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth multiline minRows={2} label="Note (goes on the ticket)" value={note} onChange={(e) => setNote(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => onClose(false)} disabled={busy}>Close</Button>
        <Button color="inherit" onClick={() => act('dismiss')} disabled={busy || !trip}>Dismiss</Button>
        <Button color="warning" onClick={() => act('personal')} disabled={busy || !trip}>Personal (unpaid)</Button>
        <Button variant="outlined" onClick={() => act('parts_pickup')} disabled={busy || !trip || !ticket}>Parts pickup for ticket</Button>
        <Button variant="contained" onClick={() => act('attach')} disabled={busy || !trip || !ticket}>Attach to ticket (work)</Button>
      </DialogActions>
    </Dialog>
  );
}
